import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { DEMO_MAILTO } from "@/lib/scroll";

const plans = [
    {
        name: "Essential",
        tagline: "Small teams getting started",
        priceLabel: "Essential",
        subLabel: "Users 1 – 10",
        testid: LANDING.pricingStarterBtn,
        featured: false,
        features: [
            "Users 1 – 10",
            "Safety Case Management",
            "Action Tracker",
            "Offline mobile capture",
            "Standard reporting",
            "Email support",
        ],
        cta: "Request a Demo",
    },
    {
        name: "Pro",
        tagline: "Growing multi-site operations",
        priceLabel: "Pro",
        subLabel: "Users 11 – 50",
        testid: LANDING.pricingProfessionalBtn,
        featured: true,
        features: [
            "Users 11 – 50",
            "Safety + Assurance + Audit",
            "Multi-site benchmarking",
            "Automated workflows",
            "Advanced analytics",
            "SSO & role-based access",
            "Priority support",
        ],
        cta: "Request a Demo",
    },
    {
        name: "Enterprise",
        tagline: "Global, regulated operators",
        priceLabel: "Enterprise",
        subLabel: "Unlimited Users",
        testid: LANDING.pricingEnterpriseBtn,
        featured: false,
        features: [
            "Unlimited Users",
            "Everything in Pro",
            "Custom fields & workflows",
            "Dedicated environment",
            "API & data warehouse export",
            "Regulatory compliance packs",
            "Named CSM + onboarding",
            "24/7 enterprise SLA",
        ],
        cta: "Talk to Sales",
    },
];

export const Pricing = () => {
    return (
        <section id="pricing" className="relative bg-[#F0F4F8] py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                <div className="max-w-3xl mb-16">
                    <span className="label-eyebrow text-[#0052CC]">// Pricing</span>
                    <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#091524] tracking-tight leading-[1.05]">
                        Priced for the way<br />you operate.
                    </h2>
                    <p className="mt-6 text-lg text-[#475569]">
                        Every deployment is customised. Choose the tier that matches your scale —
                        we tailor the rest to your industry, compliance regime, and rollout plan.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {plans.map((p, i) => (
                        <motion.div
                            key={p.name}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className={`relative flex flex-col p-8 md:p-10 rounded-sm border transition-all duration-300 hover:-translate-y-1 ${
                                p.featured
                                    ? "bg-[#091524] text-white border-[#091524] shadow-[0_20px_60px_rgba(9,21,36,0.25)]"
                                    : "bg-white text-[#091524] border-[#091524]/10 hover:shadow-[0_12px_40px_rgba(9,21,36,0.08)]"
                            }`}
                        >
                            {p.featured && (
                                <div className="absolute -top-3 left-8 bg-[#0052CC] text-white text-xs font-semibold tracking-wider px-3 py-1 rounded-sm">
                                    MOST POPULAR
                                </div>
                            )}
                            <div>
                                <div
                                    className={`label-eyebrow ${
                                        p.featured ? "text-[#0052CC]" : "text-[#0052CC]"
                                    }`}
                                >
                                    {p.name}
                                </div>
                                <div
                                    className={`mt-2 text-sm ${
                                        p.featured ? "text-white/70" : "text-[#475569]"
                                    }`}
                                >
                                    {p.tagline}
                                </div>
                                <div className="mt-8 flex items-baseline gap-2">
                                    <span className="font-display text-5xl md:text-6xl font-extrabold">
                                        {p.priceLabel}
                                    </span>
                                </div>
                                <div
                                    className={`mt-1 text-sm ${
                                        p.featured ? "text-white/60" : "text-[#475569]"
                                    }`}
                                >
                                    {p.subLabel}
                                </div>
                            </div>

                            <div
                                className={`my-8 h-px w-full ${
                                    p.featured ? "bg-white/10" : "bg-[#091524]/10"
                                }`}
                            />

                            <ul className="space-y-3 flex-1">
                                {p.features.map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-3 text-sm leading-relaxed"
                                    >
                                        <span
                                            className={`mt-0.5 shrink-0 w-5 h-5 flex items-center justify-center rounded-sm ${
                                                p.featured
                                                    ? "bg-[#0052CC] text-white"
                                                    : "bg-[#0052CC]/10 text-[#0052CC]"
                                            }`}
                                        >
                                            <Check size={12} strokeWidth={3} />
                                        </span>
                                        <span
                                            className={
                                                p.featured ? "text-white/85" : "text-[#091524]/85"
                                            }
                                        >
                                            {f}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                data-testid={p.testid}
                                href={DEMO_MAILTO}
                                className={`mt-10 py-4 rounded-sm font-semibold text-center transition-all duration-300 ${
                                    p.featured
                                        ? "bg-[#0052CC] hover:bg-[#0047B3] text-white"
                                        : "bg-[#091524] hover:bg-[#0052CC] text-white"
                                }`}
                            >
                                {p.cta}
                            </a>
                        </motion.div>
                    ))}
                </div>

                <p className="mt-12 text-center text-sm text-[#475569]">
                    Not sure which tier fits? <a href={DEMO_MAILTO} className="text-[#0052CC] font-semibold underline underline-offset-4">Book a walkthrough</a> &mdash; 30 minutes, no obligation.
                </p>
            </div>
        </section>
    );
};
